import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Custom hook for managing image loading states
 * @param {string} imageUrl - The image URL to track
 * @returns {Object} Image loading state and handlers
 */
export default function useImageLoading(imageUrl) {
  const [imageLoading, setImageLoading] = useState(Boolean(imageUrl));
  const [imageError, setImageError] = useState(false);
  const currentImageRef = useRef(null);
  const currentUrlRef = useRef(imageUrl);

  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoading(false);
    setImageError(true);
  }, []);

  const resetImageStates = useCallback(() => {
    // Clear the ref to ensure fresh state checking
    currentImageRef.current = null;
    if (imageUrl) {
      setImageLoading(true);
      setImageError(false);
    } else {
      setImageLoading(false);
      setImageError(false);
    }
  }, [imageUrl]);

  // Enhanced image load handler that handles cached images
  const enhancedImageLoad = useCallback(
    (event) => {
      if (event && event.target) {
        // Check if this is a cached image by checking the complete property
        const img = event.target;
        if (img.complete && img.naturalHeight !== 0) {
          // Image was already loaded (cached)
          handleImageLoad();
          return;
        }
      }
      handleImageLoad();
    },
    [handleImageLoad]
  );

  // Enhanced image error handler
  const enhancedImageError = useCallback(
    (_event) => {
      handleImageError();
    },
    [handleImageError]
  );

  // Create a ref callback to check for cached images when the image element is mounted
  const imageRefCallback = useCallback((imgElement) => {
    currentImageRef.current = imgElement;

    if (
      imgElement &&
      imgElement.src === currentUrlRef.current &&
      imgElement.complete &&
      imgElement.naturalHeight !== 0
    ) {
      // Image is already loaded (cached) and matches current URL
      setImageLoading(false);
      setImageError(false);
    }
  }, []);

  // Reset states when URL changes
  useEffect(() => {
    currentUrlRef.current = imageUrl;

    if (imageUrl) {
      setImageLoading(true);
      setImageError(false);

      // Check immediately if we have a cached image for this URL
      const currentImg = currentImageRef.current;
      if (
        currentImg &&
        currentImg.src === imageUrl &&
        currentImg.complete &&
        currentImg.naturalHeight !== 0
      ) {
        // Image is already loaded and cached for this exact URL
        setImageLoading(false);
      }
    } else {
      setImageLoading(false);
      setImageError(false);
    }
  }, [imageUrl]);

  return {
    handleImageError: enhancedImageError,
    handleImageLoad: enhancedImageLoad,
    imageError,
    imageLoading,
    imageRefCallback,
    resetImageStates
  };
}
